import * as React from 'react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CardListCell {
  /** The content of this cell */
  content: React.ReactNode;
  /** Optional key override — defaults to cell index */
  key?: string;
}

export interface CardListRow<TId extends string | number = string> {
  /** Cell contents in column order (must match the `gridTemplate` column count) */
  cells: CardListCell[];
  /**
   * Optional full-width block rendered below the cells inside the row card.
   * Spans all columns (`col-span-full`) with `pt-2` separation from the cell row.
   * Use this to render an `ErrorBanner` or other contextual content per row.
   */
  footer?: React.ReactNode;
  /** Unique identifier for this row */
  id: TId;
  /** Optional click handler — row becomes interactive when provided */
  onClick?: () => void;
}

export interface CardListProps<TId extends string | number = string> {
  /** Additional CSS classes on the outer wrapper */
  className?: string;
  /**
   * CSS `grid-template-columns` value shared by all rows via CSS subgrid.
   * Applied to the outer grid container; each row card inherits the tracks
   * via `grid-template-columns: subgrid`, ensuring table-like column alignment.
   * Example: `"auto 112px 128px 1fr auto"` (matches the tasks list reference).
   */
  gridTemplateColumns: string;
  /** Row data */
  rows: CardListRow<TId>[];
  /**
   * Tailwind class for the row gap between cards (default: `gap-y-2`).
   * Applied to the outer grid container. Use `gap-y-*` classes for row-only gaps.
   */
  rowGapClassName?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Compact non-paginated list where each row is a rounded bordered "bubble" card.
 *
 * - No header row.
 * - All rows share identical column tracks via CSS subgrid, giving table-like
 *   alignment while preserving the individual card visual.
 * - The outer container is a CSS grid with `gridTemplateColumns`; each card
 *   spans all columns (`col-span-full`) and uses `grid-template-columns: subgrid`
 *   so cells in every row align to the same tracks.
 * - Rows are optionally clickable.
 * - Generic over the row id type for type-safe `key` usage.
 */
export function CardList<TId extends string | number = string>({
  className,
  gridTemplateColumns,
  rowGapClassName = 'gap-y-2',
  rows,
}: CardListProps<TId>): React.JSX.Element {
  return (
    <div className={cn('grid gap-x-3', rowGapClassName, className)} style={{ gridTemplateColumns }}>
      {rows.map((row) => {
        const isClickable = row.onClick !== undefined;

        function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            row.onClick?.();
          }
        }

        return (
          <div
            className={cn(
              'col-span-full rounded-xl border overflow-hidden bg-card border-border',
              'grid grid-cols-subgrid items-center px-3 py-2.5',
              isClickable && 'cursor-pointer hover:bg-accent transition-colors',
            )}
            key={row.id}
            onClick={isClickable ? row.onClick : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
          >
            {row.cells.map((cell, cellIndex) => (
              <React.Fragment key={cell.key ?? cellIndex}>{cell.content}</React.Fragment>
            ))}
            {row.footer !== undefined && <div className="col-span-full pt-2">{row.footer}</div>}
          </div>
        );
      })}
    </div>
  );
}
